import { Navigate, useParams } from "react-router";
import RoundedCard from "@cle/components/RoundedCard";
import TableError from "@cle/components/TableError";
import { useInjunction } from "@cle/hooks/useInjunction";
import { usePaalaeg } from "@cle/hooks/usePaalaeg";
import type { AfgorelseType } from "@/interfaces/afgorelse";
import { eftPath } from "@cle/constants/routes";
import InactiveAfgorelseDetailView from "./InactiveAfgorelseDetailView";
import { resolveAfgorelseDetailVariant } from "./resolveAfgorelseDetailVariant";
import PendingAfgorelseDetailView from "./PendingAfgorelseDetailView";
import SubmittedAfgorelseDetailView from "./SubmittedAfgorelseDetailView";

const isAfgorelseType = (value: string | undefined): value is AfgorelseType =>
  value === "paabud" || value === "paalaeg";

const AfgorelseDetailView = () => {
  const { type, id } = useParams<{ type?: string; id?: string }>();

  if (!isAfgorelseType(type) || !id) {
    return <Navigate replace to={eftPath("afgorelser")} />;
  }

  if (type === "paabud") {
    return <PaabudAfgorelseDetailView id={id} />;
  }

  return <PaalaegAfgorelseDetailView id={id} />;
};

const AfgorelseDetailLoading = () => (
  <div className="cle-report-detail">
    <RoundedCard>
      <span className="visually-hidden">Indlæser afgørelse</span>
      <div className="placeholder-glow">
        <span className="placeholder col-5 rounded mb-3 d-block" />
        <span className="placeholder col-8 rounded mb-2 d-block" />
        <span className="placeholder col-6 rounded d-block" />
      </div>
    </RoundedCard>
  </div>
);

const AfgorelseDetailContent = ({
  afgorelseType,
  detail,
  isError,
  isLoading,
}: {
  afgorelseType: AfgorelseType;
  detail:
    | ReturnType<typeof useInjunction>["data"]
    | ReturnType<typeof usePaalaeg>["data"];
  isError: boolean;
  isLoading: boolean;
}) => {
  if (isLoading) {
    return <AfgorelseDetailLoading />;
  }

  if (isError) {
    return (
      <div className="cle-report-detail">
        <TableError message="Afgørelsen kunne ikke indlæses lige nu." />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="cle-report-detail">
        <div className="alert alert-info mb-0" role="status">
          Afgørelsen blev ikke fundet.
        </div>
      </div>
    );
  }

  const variant = resolveAfgorelseDetailVariant(detail);

  if (variant === "pending") {
    return (
      <PendingAfgorelseDetailView
        afgorelseType={afgorelseType}
        detail={detail}
      />
    );
  }

  if (variant === "submitted") {
    return (
      <SubmittedAfgorelseDetailView
        afgorelseType={afgorelseType}
        detail={detail}
      />
    );
  }

  return (
    <InactiveAfgorelseDetailView
      afgorelseType={afgorelseType}
      detail={detail}
      variant={variant}
    />
  );
};

const PaabudAfgorelseDetailView = ({ id }: { id: string }) => {
  const { data, isError, isLoading } = useInjunction(id);

  return (
    <AfgorelseDetailContent
      afgorelseType="paabud"
      detail={data}
      isError={isError}
      isLoading={isLoading}
    />
  );
};

const PaalaegAfgorelseDetailView = ({ id }: { id: string }) => {
  const { data, isError, isLoading } = usePaalaeg(id);

  return (
    <AfgorelseDetailContent
      afgorelseType="paalaeg"
      detail={data}
      isError={isError}
      isLoading={isLoading}
    />
  );
};

export default AfgorelseDetailView;
